import { getCookie } from '@api/HelperAPI';
import { useGetContext } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getAccountIsLoanEnded } from '@helpers/Accounts/getAccountIsLoanEnded';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import {
    fetchGetApplication,
    fetchGetProfile,
    GetAccountsResponse
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { TNavigationData } from './Navigation.types';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import {
    getDefaultDashboardLocation,
    hasIwocaPayApplication,
    hasValidIwocaPayFormalOffer
} from '../dashboard/getDefaultDashboardLocation';
import { MY_OFFERS_URL } from '../myOffers/MyOffers.sagas';

export const useGetNavigationData = (): Partial<TNavigationData> => {
    const stateKey = useStateKey();

    const { data: contextData } = useGetContext();

    const isAccessUser = contextData?.logged_in_user_type === 'access_user';

    const {
        data: getApplicationResponse,
        isLoading: applicationLoading,
        isError: applicationError
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey })
    });

    const {
        isLoading: accountsLoading,
        isError: accountsError,
        data: getAccountsResponse
    } = useQuery(getAccountsQueryConfig(stateKey));

    const { data: getProfileResponse, isLoading: loadingProfileData } =
        useQuery({
            queryKey: ['profile'],
            queryFn: () => fetchGetProfile({})
        });

    const userId = getProfileResponse?.data?.user_id;

    const { data: creditCompassData, isFetching: loadingCreditCompassData } =
        useQuery({
            queryKey: ['creditCompassProfile', userId],
            queryFn: () => getCreditCompass(userId as number),
            enabled: !!userId && getCountry() === 'UK'
        });

    const isLoading =
        accountsLoading ||
        applicationLoading ||
        loadingProfileData ||
        loadingCreditCompassData;
    const isError = accountsError || applicationError;

    if (getAccountsResponse && getApplicationResponse) {
        const accountsProductTypes =
            getAccountsProductTypes(getAccountsResponse);

        const defaultLocation =
            getApplicationResponse.data &&
            getDefaultDashboardLocation(
                getApplicationResponse,
                getAccountsResponse
            );
        let shouldShowMyOffers = true;

        shouldShowMyOffers = defaultLocation?.pathname === MY_OFFERS_URL;

        const hasIwocaPayApplicationOrFormalOffer =
            hasIwocaPayApplication(getApplicationResponse) ||
            hasValidIwocaPayFormalOffer(getAccountsResponse);

        return {
            accountsResponse: getAccountsResponse,
            applicationResponse: getApplicationResponse,
            isLoading: isLoading,
            isError: isError,
            showCreditCard: Boolean(getCookie('credit_card_dashboard')),
            showRBL:
                !isAccessUser &&
                accountsProductTypes.includes('merchant_cash_advance') &&
                !shouldShowMyOffers,
            showIwocaPay:
                !isAccessUser &&
                (accountsProductTypes.includes('iwocapay') ||
                    hasIwocaPayApplicationOrFormalOffer),
            showTermLoan:
                !isAccessUser &&
                accountsProductTypes.includes('term_loan') &&
                !isAccountLoanEnded(getAccountsResponse, 'term_loan'),
            showRLS:
                !isAccessUser && accountsProductTypes.includes('recovery_loan'),
            showCBILS: !isAccessUser && accountsProductTypes.includes('cbils'),
            showSecondCBILS:
                !isAccessUser &&
                accountsProductTypes.filter(
                    (productType) => productType === 'cbils'
                ).length === 2,
            showFlexiLoan:
                !isAccessUser &&
                (accountsProductTypes.includes('credit_facility') ||
                    accountsProductTypes.includes('flexi24')) &&
                !shouldShowMyOffers,
            showMyOffers:
                isAccessUser ||
                shouldShowMyOffers ||
                (!accountsProductTypes.includes('credit_facility') &&
                    !accountsProductTypes.includes('flexi24')),
            showCreditCompass:
                !isAccessUser &&
                creditCompassData?.company_numbers &&
                creditCompassData?.company_numbers.length > 0
        };
    } else
        return {
            isLoading: isLoading,
            isError: isError
        };
};

const getAccountsProductTypes = (accountsResponse: GetAccountsResponse) => {
    const productTypes = [];

    for (const account of accountsResponse.data.accounts) {
        if (!account.product_type || !account.account_id) continue;

        productTypes.push(account.product_type);
    }

    return productTypes;
};

const isAccountLoanEnded = (
    accountsResponse: GetAccountsResponse,
    productType: string
) => {
    const accounts = accountsResponse.data.accounts;
    const accountForProductType = accounts.find(
        (accounts) => accounts.product_type === productType
    );
    if (!accountForProductType) {
        return false;
    }
    return getAccountIsLoanEnded(accountForProductType);
};

interface CreditCompassProfile {
    name: string;
    email: string;
    company_numbers?: string[];
}

const getCreditCompass = async (
    profileId: string | number
): Promise<CreditCompassProfile | null> => {
    // eslint-disable-next-line iwoca/prefer-lapi-client
    const response = await fetch(`/api/credit-compass/users/${profileId}`, {
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
        }
    });

    // Handle 404 as expected behavior (customer not found)
    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
};
